/** Workspace tabs (KTD11) — Opportunities | Scripts | Sent to vendor. */
export type PersonaOpportunityTabKey = "opportunities" | "scripts" | "sent_to_vendor";

/** Opportunities-tab Cards / Table preference — persisted in the route search param. */
export type PersonaOpportunityViewMode = "cards" | "table";

export const PERSONA_OPPORTUNITY_TAB_KEYS: [PersonaOpportunityTabKey, ...PersonaOpportunityTabKey[]] = [
  "opportunities",
  "scripts",
  "sent_to_vendor",
];

export type PersonaOpportunityGenerationStatus = "GENERATING" | "READY" | "FAILED";

/**
 * Status filter on the Opportunities tab only.
 * `all` = every opportunity; otherwise match `PersonaOpportunityStatus`.
 */
export type PersonaOpportunityStatusFilter =
  | "all"
  | "NEW"
  | "SCRIPTS_GENERATED"
  | "BRIEFS_GENERATED"
  | "SCRIPTS_AND_BRIEFS_GENERATED"
  | "SENT_TO_VENDOR";

export interface PersonaOpportunityTabCounts {
  opportunities: number;
  scripts: number;
  sent_to_vendor: number;
}

export const EMPTY_PERSONA_OPPORTUNITY_TAB_COUNTS: PersonaOpportunityTabCounts = {
  opportunities: 0,
  scripts: 0,
  sent_to_vendor: 0,
};

/** Optional advanced knobs sent with generate when the tenant has query-tuning permission. */
export interface PersonaOpportunityQueryTuning {
  candidatePoolSize: number;
  anglesReturned: number;
  maxAnglesPerFeature: number;
  minBrandsPerFeature: number;
  stickDays: number;
}

export type PersonaDataWindowPreset = "last_30_days" | "last_60_days" | "last_90_days" | "custom";

/**
 * Set-level view model the page tree renders. Built from the API by
 * `lib/persona-opportunity-set-mapper.ts` — the config snapshot is flattened and
 * dates stay calendar `YYYY-MM-DD` strings.
 */
export interface PersonaOpportunitySet {
  id: string;
  name: string;
  tenantId: string;
  industry: string | null;
  industryCompetitors: string[];
  adPlatform: string | null;
  status: PersonaOpportunityGenerationStatus;
  /** Calendar `YYYY-MM-DD` when the set has a stored window. */
  startDate: string | null;
  endDate: string | null;
  /** Total opportunities on the set — from list `_count` or detail sum. */
  opportunityCount: number;
  /** Non-deleted persona scripts on the set — from list/detail `scriptCount`. */
  scriptCount: number;
  /**
   * Tab badge counts. `null` on list rows (list has totals only); filled once
   * the detail query resolves.
   */
  counts: PersonaOpportunityTabCounts | null;
  createdAt: string;
  /** Last write time — used as generated-age until `generationCompletedAt` exists on the API. */
  updatedAt: string;
}

/** Single-step create dialog form state. */
export interface CreatePersonaSetFormValues {
  name: string;
  industry: string;
  industryCompetitors: string[];
  adPlatform: string;
  personaIds: string[];
  /** Committed `YYYY-MM-DD` pair. Defaults to last 60 days when the modal opens. */
  startDate: string;
  endDate: string;
  activePresetId: string;
  dataWindowPreset: PersonaDataWindowPreset;
  customDaysBack?: number;
  showAdvanced: boolean;
  advanced: PersonaOpportunityQueryTuning;
}
